<template>
  <div class="multi-select" ref="root">
    <!-- Trigger / display -->
    <button type="button" class="multi-select__button" @click="isOpen = !isOpen">
      <div class="multi-select__placeholder">{{ displayLabel }}</div>
      <div class="multi-select__caret">
        <i v-if="isOpen" class="fa-solid fa-caret-up"></i>
        <i v-else class="fa-solid fa-caret-down"></i>
      </div>
    </button>
    <!-- Dropdown -->
    <div v-if="isOpen" class="multi-select__dropdown">
      <!-- Search -->
      <input v-if="options.length > 5 && filter"
        v-model="search"
        type="text"
        class="multi-select__search"
        :placeholder="filterPlaceholder"
        @keyup.enter.stop
      />

      <!-- Controls -->
      <div class="multi-select__controls">
        <label class="multi-select__option">
          <input class="multi-select__input" type="checkbox" :checked="allSelected" @change="toggleSelectAll"/>All
        </label>
        <button type="button" class="multi-select__clear" @click="clearSelection">
          Clear
        </button>
      </div>

      <!-- Options -->
      <div class="multi-select__options">
        <label
          v-for="option in filteredOptions"
          :key="option[valueKey]"
          class="multi-select__option"
        >
          <input class="multi-select__input"
            type="checkbox"
            :value="option[valueKey]"
            :checked="localValue.includes(option[valueKey])"
            @change="toggleOption(option[valueKey])"
          />
          <span>{{ option[labelKey] }}</span>
        </label>

        <div v-if="filteredOptions.length === 0" class="multi-select__empty">
          No options
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  options: {
    type: Array,
    default: () => [] // [{ label: 'Illinois', value: 'IL' }, ...]
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  placeholder: {
    type: String,
    default: 'None'
  },
  filter: {
    type: Boolean,
    default: true
  },
  filterPlaceholder: {
    type: String,
    default: 'Search'
  },
  maxHeight: {
    type: Number,
    default: 200
    }
  })

const emit = defineEmits(['update:modelValue'])

const root = ref(null)
const isOpen = ref(false)
const search = ref('')
const localValue = ref([...props.modelValue])

// Close dropdown on outside click
function handleClickOutside(event) {
  //  If click is not inside this current component, close the dropdown
  if (root.value && !root.value.contains(event.target)) {
    isOpen.value = false
  }
}

// Set up and clean up event listener
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

// keep localValue in sync if parent changes it externally
watch(
  () => props.modelValue,
  (newVal) => {
    localValue.value = [...newVal]
  }
)

// computed display label
const displayLabel = computed(() => {
  const count = selectedCount.value
  if (count === 0) {
    return props.placeholder
  } else if (count === props.options.length) {
    return 'All'
  } else if (count <= 3) {
    // Show the selected options
    const selectedOptions = props.options
      .filter(opt => localValue.value.includes(opt[props.valueKey]))
      .map(opt => opt[props.labelKey])
    return selectedOptions.join(', ')
  } else {
    return `${count} of ${props.options.length}`
  }
})


const filteredOptions = computed(() => {
  if (!props.filter || !search.value.trim()) {
    return props.options
  }
  const term = search.value.toLowerCase()
  return props.options.filter((opt) =>
    String(opt[props.labelKey]).toLowerCase().includes(term)
  )
})

const selectedCount = computed(() => localValue.value.length)

const allSelected = computed(() => {
  return props.options.length > 0 &&
    localValue.value.length === props.options.length
})

function toggleOption(value) {
  const index = localValue.value.indexOf(value)
  if (index === -1) {
    localValue.value.push(value)
  } else {
    localValue.value.splice(index, 1)
  }
  emit('update:modelValue', [...localValue.value])
}

function toggleSelectAll() {
  if (allSelected.value) {
    localValue.value = []
  } else {
    localValue.value = props.options.map((opt) => opt[props.valueKey])
  }
  emit('update:modelValue', [...localValue.value])
}

function clearSelection() {
  localValue.value = []
  emit('update:modelValue', [])
}
</script>

<style scoped>
.multi-select {
  position: relative;
  width: 100%;
}

/* Button */
.multi-select__button {
  width: 100%;
  text-align: left;
  padding: 0.25rem 0.6rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  background: #fff;
  font-size: 0.8rem;
  overflow: hidden;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.1rem;
}

/* Placeholder */
.multi-select__placeholder {
  overflow: hidden;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  width: calc(100% - 1.5rem);
  line-height: 1;
  color: var(--color-text-light);
}

.multi-select__caret {
  color: var(--color-text-light);
}

/* Dropdown */
.multi-select__dropdown {
  max-width: 250px;
  position: absolute;
  z-index: 10;
  width: 100%;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: var(--shadow-md);
  margin-top: 0.25rem;
  padding: 0.4rem;
}

/* Search */
.multi-select__search {
  width: 100%;
  margin-bottom: 0.4rem;
  padding: 0.15rem 0.25rem;
  font-size: 0.75rem;
  border-radius: 4px;
  border: 1px solid #ccc;
}

/* Controls (select all / clear) */
.multi-select__controls {
  display: flex;
  justify-content: flex-start;
  gap: var(--space-4);
  align-items: center;
  font-size: 0.8rem;
  padding: 0.5rem 0;
  margin-bottom: 0.4rem;
  border-bottom: 1px solid #ccc;
}

.multi-select__clear {
  box-sizing: content-box;
  width: auto;
  padding: 0.1rem 0.5rem;
  background: transparent;
  color: var(--color-secondary);
  border: 0.5px solid var(--color-secondary);  
  font-size: 0.75rem;
  cursor: pointer;
  line-height: 1;
}

/* Options list */
.multi-select__options {
  max-height: 200px; /* you can tie this to props.maxHeight if you want */
  overflow-y: auto;
}

.multi-select__option {
  line-height: 1.5;
  display: flex;
  gap: 0.35rem;
  font-size: 0.75rem;
  padding: 0.15rem 0;
  cursor: pointer;
  align-items: flex-start;
  align-self: center;
  vertical-align: text-bottom;
  font-weight: 400;
}

.multi-select__input {
  margin-top: 3px;
  cursor: pointer;
  height: 0.75rem;
  width: 0.75rem; 
}

.multi-select__empty {
  font-size: 0.8rem;
  color: #888;
  padding: 0.25rem 0;
}
</style>
